interface Props {
  /** Selected month as 'YYYY-MM'. */
  selected: string;
  /** Months to show as chips, oldest first. */
  months: string[];
  onSelect: (month: string) => void;
  /** Optional inclusive floor — disables the `‹` arrow at the boundary. */
  minMonth?: string;
  /** Optional inclusive ceiling — disables the `›` arrow at the boundary. */
  maxMonth?: string;
}

/**
 * Format 'YYYY-MM' as a short human label, e.g. '2026-05' → 'May 2026'.
 * Falls back to the raw string if parsing fails.
 */
function formatLabel(month: string): string {
  const date = new Date(`${month}-01T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return month;
  return date.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Add `delta` calendar months to a 'YYYY-MM' string. Returns 'YYYY-MM'.
 */
function shiftMonth(month: string, delta: number): string {
  const date = new Date(`${month}-01T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return month;
  date.setUTCMonth(date.getUTCMonth() + delta);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

export function MonthChipStrip({
  selected,
  months,
  onSelect,
  minMonth,
  maxMonth,
}: Props): JSX.Element {
  const prev = shiftMonth(selected, -1);
  const next = shiftMonth(selected, 1);
  // String comparison is sufficient for the 'YYYY-MM' format.
  const prevDisabled = minMonth !== undefined && selected <= minMonth;
  const nextDisabled = maxMonth !== undefined && selected >= maxMonth;

  return (
    <div className="month-chip-strip" role="tablist" aria-label="Month selector">
      <button
        type="button"
        className="month-chip-arrow"
        onClick={() => onSelect(prev)}
        disabled={prevDisabled}
        title={prevDisabled ? 'No earlier data' : `Go to ${formatLabel(prev)}`}
        aria-label={prevDisabled ? 'No earlier data' : `Previous month: ${formatLabel(prev)}`}
      >
        ‹
      </button>
      <div className="month-chip-list">
        {months.map((month) => {
          const isSelected = month === selected;
          return (
            <button
              key={month}
              type="button"
              role="tab"
              aria-selected={isSelected}
              className={isSelected ? 'month-chip month-chip-selected' : 'month-chip'}
              onClick={() => onSelect(month)}
            >
              {formatLabel(month)}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="month-chip-arrow"
        onClick={() => onSelect(next)}
        disabled={nextDisabled}
        title={nextDisabled ? 'No future data yet' : `Go to ${formatLabel(next)}`}
        aria-label={nextDisabled ? 'No future data yet' : `Next month: ${formatLabel(next)}`}
      >
        ›
      </button>
    </div>
  );
}
